<script setup lang="ts" generic="T, C extends ColumnModel, R extends RecordMap<C>">
import { PV } from "@/widgets";
import { isNil } from "es-toolkit";
import { computed } from "vue";
import { useProfileStore } from "../../stores/profile.store";
import type { ColumnModel, RecordMap, TableColumn, defineTable } from "../common";
import { CustomTable } from "../internal";

const props = defineProps<{
    table: ReturnType<typeof defineTable<T, C, R>>;
    expandable?: boolean;
    watchRefresh?: unknown;
}>();

const slots = defineSlots<
    TableColumn<R> & {
        _tools: () => any;
        _title: () => any;
        _buttons: () => any;
    }
>();

const profile = useProfileStore().data;

const tableCols = computed(() => {
    const kind = profile?.kind;

    let keys: Set<keyof R>;
    if (isNil(kind)) {
        keys = new Set([]);
    } else {
        let currentSet = props.table.visible[kind];
        if (currentSet.length === 0) {
            currentSet = props.table.columns.map((c) => c.field);
        }

        keys = new Set(currentSet);
    }

    return props.table.columns.filter((c) => keys.has(c.field));
});

function castName(name: string) {
    return name as keyof typeof slots;
}
</script>

<template>
    <CustomTable :columns="tableCols" :table="props.table" :watchRefresh="props.watchRefresh">
        <template #headers="{ refresh }">
            <div class="flex flex-row items-center justify-center py-1">
                <div class="w-3/12 flex flex-row justify-start">
                    <PV.Button icon="pi pi-refresh" severity="secondary" size="small" text @click="refresh" />
                    <slot name="_tools"></slot>
                </div>

                <div class="grow flex flex-row items-center justify-center">
                    <slot name="_title"></slot>
                </div>

                <div class="w-3/12 flex flex-row justify-end">
                    <slot name="_buttons"></slot>
                </div>
            </div>
        </template>

        <template v-for="column in tableCols">
            <PV.Column class="whitespace-nowrap" :field="column.field" :header="column.title">
                <template v-if="slots[castName(column.field)] !== undefined" #body="{ data }">
                    <slot :data="data" :name="castName(column.field)" />
                </template>
            </PV.Column>
        </template>
    </CustomTable>
</template>
